import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject, Subject, EMPTY, firstValueFrom, of, takeUntil, throwError, catchError, tap, switchMap } from 'rxjs';
import { AuthProvider, isDebugMode, isProviderEnabled } from '../../config/sso.config';
import { environment } from '@environments/environment';
import { UserProfile } from '../models';
import { GlobalAuthStateService } from './global-auth-state.service';
import { LoggerService } from './logger.service';
import { MicrosoftAuthService, MicrosoftUser } from './microsoft-auth.service';
import { GoogleAuthService, GoogleUser } from './google-auth.service';
import { SsoLinkBridge } from './sso-link-bridge.service';
import { MfaChallengeBridge } from '../../routes/mfa-challenge/mfa-challenge.component';

/**
 * Error surfaced when an SSO exchange fails (task 4.3, Defect 1).
 *
 * A failed Microsoft/Google exchange must be a VISIBLE failed login — never a
 * silent return to the login screen. The login component reads
 * `ssoExchangeError$` to render the error banner.
 */
export class SsoExchangeError extends Error {
  constructor(
    message: string,
    readonly provider: 'microsoft' | 'google',
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'SsoExchangeError';
  }
}

/**
 * Unified user profile interface
 *
 * Common interface that normalizes user data across different authentication providers.
 */
export interface UnifiedUser {
  id: string;
  email: string;
  name: string;
  displayName: string;
  provider: AuthProvider;
  avatar?: string;
  originalProfile: MicrosoftUser | GoogleUser;
  /**
   * TOTP MFA state from whoami (add-parent-portal-passkey-mfa): 'confirmed'
   * (active second factor), 'unconfirmed' (enrolled, never verified), or
   * 'disabled' (no usable TOTP secret). Absent from SSO-provider profiles —
   * only whoami populates it.
   */
  mfaStatus?: 'confirmed' | 'unconfirmed' | 'disabled';
}

/**
 * Result of POST /parent-auth/login (step 1 of the password + MFA flow).
 *
 * The backend NEVER establishes a session at this step: a correct password
 * yields `{ mfaRequired: true, challengeToken }` (plus `mfaEnrollmentRequired`
 * when the account has no confirmed TOTP secret yet). The challengeToken is a
 * short-lived, single-use, challenge-purpose token — NOT a credential and NOT
 * stored anywhere persistent (kept in component state only, per design.md).
 */
export interface LoginResult {
  mfaRequired?: boolean;
  mfaEnrollmentRequired?: boolean;
  challengeToken?: string;
  /**
   * True when the contact has at least one ACTIVE passkey (Phase 2, task
   * 2.2) — the client defaults the MFA-challenge screen to the passkey
   * option. Absent/false keeps the TOTP-only flow byte-for-byte unchanged.
   */
  hasPasskey?: boolean;
}

/** POST /parent-auth/mfa/enroll response body (`data` of the envelope). */
export interface MfaEnrollResult {
  otpauthUri: string;
}

/**
 * POST /parent-auth/passkey/registration-options response body.
 *
 * `publicKeyCredentialOptions` is the serialized PublicKeyCredentialCreationOptions
 * JSON produced by the backend's java-webauthn-server — passed verbatim to
 * `startRegistration()` from `@simplewebauthn/browser`, then round-tripped back
 * to `passkeyRegistrationVerify()` (the library requires the ORIGINAL options
 * object to verify the attestation). Held in component state only.
 */
export interface PasskeyRegistrationOptions {
  publicKeyCredentialOptions: string;
}

/** POST /parent-auth/passkey/registration-verify response body. */
export interface PasskeyRegistrationResult {
  registered: boolean;
  credentialId: string;
}

/** GET /parent-auth/passkey/credentials response body (one row per credential). */
export interface PasskeyCredential {
  id: number;
  nickname: string;
  createdAt: number;
  lastUsedAt?: number | null;
}

/** POST /parent-auth/passkey/step-up response body (security_action proof). */
export interface PasskeyStepUpResult {
  stepUpToken: string;
}

/**
 * POST /parent-auth/passkey/authentication-options response body (Phase 2).
 *
 * `publicKeyCredentialRequestOptions` is the browser-facing JSON (from the
 * backend's toCredentialsGetJson()) — passed verbatim to startAuthentication()
 * from @simplewebauthn/browser. `waChallengeToken` is the passkey_assertion
 * ceremony token consumed by passkeyAuthenticationVerify(). Both are held in
 * component state only.
 */
export interface PasskeyAuthenticationOptions {
  publicKeyCredentialRequestOptions: string;
  waChallengeToken: string;
}

/** GET /parent-auth/account-setup/{token} response body (display context). */
export interface AccountSetupContext {
  email: string;
}

/** POST /parent-auth/forgot-password response body — constant for all emails. */
export interface ForgotPasswordResult {
  accepted: boolean;
}

/** POST /parent-auth/reset-password response body. */
export interface ResetPasswordResult {
  reset: boolean;
}

/** GET /parent-auth/whoami response body (`data` of the backend envelope). */
export interface WhoAmIResponse {
  contact: {
    id: number | string;
    email: string;
    name: string;
    displayName: string;
  };
  mfaStatus: 'confirmed' | 'unconfirmed' | 'disabled';
  /** 4.19/4.20: false for a Google/Microsoft-only account (no password to change). */
  hasPassword?: boolean;
  ssoConnections: Array<{ provider: 'microsoft' | 'google'; connectedAt: string }>;
  /** 2.7 (DCMPDEV-682): pending parent (active=0, open enrollment). */
  pendingEnrollment?: boolean;
  /** 2.7: the parent's open enrollments (2.10 adds the submitted/returned fields). */
  enrollments?: Array<{
    enrollmentId: number;
    childFirstName: string;
    status: 'enrolling' | 'forms_complete' | 'pending_start';
    /** 2.10: epoch seconds when the parent submitted to the centre (0 when not). */
    submittedAt?: number;
    /** 2.10: the reason staff sent the forms back ('' when not returned). */
    returnedReason?: string;
    /** 2.10: epoch seconds when the return-to-enrolling transition happened (0 otherwise). */
    returnedAt?: number;
  }>;
  /**
   * 3.3 (DCMPDEV-767, U3): the parent's authorized ACTIVE children — the
   * "My child's forms" entry points and the my-forms child-name labels.
   */
  children?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    name: string;
  }>;
}

/**
 * Raw response envelope used by the GTLC ColdFusion REST backend:
 * `{ statusCode, data, statusMessage }`.
 */
interface BackendEnvelope<T> {
  statusCode: number;
  data: T;
  statusMessage?: string;
}

/**
 * Multi-Provider Authentication Service
 *
 * Centralized authentication service that manages SSO providers through
 * dedicated provider services and the GTLC backend's cookie-session model.
 *
 * Session model (design.md Decision 1): the backend issues an httpOnly
 * `gtlc_parent_session` cookie after a successful SSO exchange or MFA
 * verification. The browser never sees or stores the session token; every
 * API call carries the cookie automatically via `withCredentials: true`
 * in `api.service.ts`. There is deliberately NO bearer token, NO token in
 * localStorage/sessionStorage, and NO client-side session state that can
 * be forged — authenticated UI state is derived exclusively from the
 * backend's answer to `GET /parent-auth/whoami`.
 *
 * Key Features:
 * - whoami-backed boot state: `checkInitialAuthState()` asks the BACKEND,
 *   not the MSAL/Google SDKs, whether a valid session exists
 * - Provider-specific services for the SSO token-acquisition step only
 * - Password + TOTP MFA login (`loginPassword` / `verifyMfa`)
 * - Reactive authentication state management
 * - Logout revokes the server-side session AND clears client state
 *
 * Architecture:
 * - MicrosoftAuthService: acquires a Microsoft ID token (MSAL)
 * - GoogleAuthService: acquires a Google ID token (GIS)
 * - AuthService: exchanges tokens with the backend, owns session state
 *
 * @injectable
 * @providedIn 'root'
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly microsoftAuthService = inject(MicrosoftAuthService);
  private readonly googleAuthService = inject(GoogleAuthService);
  private readonly globalAuthState = inject(GlobalAuthStateService);
  private readonly ssoLinkBridge = inject(SsoLinkBridge);
  private readonly logger = inject(LoggerService);

  /**
   * Whether an account-setup SSO link is pending (staged before the MSAL
   * redirect). Read by the root-redirect guard to route the redirect
   * landing back into the setup flow's resume mode. Non-consuming peek.
   */
  get ssoLinkPending(): boolean {
    return this.ssoLinkBridge.peek() !== null;
  }

  private readonly apiUrl = environment.apiUrl;
  private readonly destroy$ = new Subject<void>();
  private readonly _isAuthenticated = new BehaviorSubject<boolean>(false);
  private readonly _userProfile = new BehaviorSubject<UnifiedUser | null>(null);
  private readonly _currentProvider = new BehaviorSubject<AuthProvider | null>(null);
  private readonly _ssoExchangeError = new Subject<SsoExchangeError>();
  // ReplaySubject(1): the SSO exchange runs at boot (the redirect result
  // arrives before the login screen mounts), so the challenge emission can
  // fire BEFORE the login component subscribes — a plain Subject drops it
  // and the user sees nothing. Replay delivers the pending challenge to
  // the late subscriber; the login screen's navigation consumes it.
  private readonly _ssoMfaChallenge = new ReplaySubject<{ provider: 'microsoft' | 'google' }>(1);

  /**
   * Boot sequencing guard (task 4.3, Defect 1).
   *
   * AuthService's constructor fires `checkInitialAuthState()` (whoami) while
   * MSAL is concurrently resolving the login redirect. Without a guard, the
   * in-flight whoami's 401 → clearAuthenticationState() could land AFTER a
   * successful exchange and wipe the fresh session state.
   *
   * The rule: a successful exchange/whoami CONFIRMATION always wins over an
   * in-flight boot whoami FAILURE. The boot whoami's clear is skipped when an
   * exchange has already (or is about to) establish a session.
   */
  private bootWhoamiInFlight = true;
  private exchangeInProgress = false;
  /** Public mirror of `exchangeInProgress` — lets a route show a loading
   *  state instead of flashing its normal UI while a redirect's SSO
   *  exchange is still resolving in the background. */
  private readonly _exchangeInProgress = new BehaviorSubject<boolean>(false);
  readonly exchangeInProgress$ = this._exchangeInProgress.asObservable();

  /** Synchronous read of `exchangeInProgress$` — lets the root guard decide
   *  whether to wait for the SSO exchange before routing (see rootRedirectGuard). */
  get isExchangeInProgress(): boolean {
    return this._exchangeInProgress.value;
  }

  /** Observable stream of authentication state changes across all providers */
  readonly isAuthenticated$ = this._isAuthenticated.asObservable();

  /** Observable stream of unified user profile changes */
  readonly userProfile$ = this._userProfile.asObservable();

  /** Observable stream of current authentication provider changes */
  readonly currentProvider$ = this._currentProvider.asObservable();

  /**
   * Observable stream of SSO exchange failures (task 4.3, Defect 1).
   *
   * The login component subscribes to render a visible error banner — a
   * failed exchange is a FAILED login, never a silent bounce.
   */
  readonly ssoExchangeError$ = this._ssoExchangeError.asObservable();

  /**
   * Observable stream of SSO logins that require the GTLC MFA step.
   *
   * The backend's SSO exchange NEVER creates a session directly — it returns
   * `{ mfaRequired: true, challengeToken, hasPasskey? }` (design.md Decision
   * 2: the SSO identity is the first factor; the GTLC second factor — TOTP or
   * passkey — is mandatory). This stream emits after the challenge token has
   * been staged in MfaChallengeBridge; the login screen subscribes and
   * navigates to /mfa-challenge (same handoff as the password path).
   */
  readonly ssoMfaChallenge$ = this._ssoMfaChallenge.asObservable();

  constructor() {
    this.initializeProviderListeners();
    this.initializeMicrosoftRedirectExchange();
    this.checkInitialAuthState();
    // Detect a Microsoft redirect response SYNCHRONOUSLY, before MSAL's own
    // async processing (or the exchange HTTP call) has even started. The
    // root-redirect-guard decides to route to /login immediately, based on
    // the CURRENT (not-yet-resolved) auth state, with no awareness that a
    // redirect is about to be exchanged — so `exchangeInProgress$` was still
    // false for LoginComponent's very first render, flashing the full form
    // before flipping to the settling spinner (observed live 2026-09-15).
    // The SSO-link (account-setup) flow has its own resume UI and never
    // sets this back to false, so it's excluded here.
    if (!this.ssoLinkPending && isMsalRedirectHash(window.location.hash)) {
      this._exchangeInProgress.next(true);
    }
    this.initializeMsalAtBoot();
  }

  /**
   * Initialize MSAL at boot on EVERY page load (Follow-up #9).
   *
   * MSAL is LAZY: `handleRedirectObservable()` only runs inside
   * `initialize()`, and MSAL caches the returned auth hash (URL_HASH
   * temporary cache) expecting the NEXT page load to call
   * `handleRedirectPromise` again. The only eager initializer was the
   * root-redirect guard — and only when an SSO LINK was pending (the
   * account-setup flow). A plain LOGIN redirect landing at `/` was routed
   * to /login with nothing ever calling initialize(): the cached hash sat
   * unprocessed, the redirect result never fired, and the exchange never
   * ran — the user had to click "Sign in with Microsoft" a second time
   * (observed live 2026-09-11: backend log shows boot whoamis with ZERO
   * sso/microsoft attempts after the redirect round-trip).
   *
   * Fire-and-forget: initialization is idempotent (the service memoizes
   * its promise), failures are logged inside initializeMsal, and the
   * redirect-result subscription above consumes whatever it produces.
   * The settle-wait paths (signInWithRedirect, root guard) already await
   * initialize() themselves, so ordering is preserved.
   */
  private initializeMsalAtBoot(): void {
    void this.microsoftAuthService.initialize().catch((error: unknown) => {
      this.logger.error('Auth Service: MSAL boot initialization failed:', error);
    });
  }

  /**
   * Initialize listeners for the SSO provider services
   *
   * Provider services only report token-acquisition state; they can NEVER
   * flip this service into authenticated state on their own. Authentication
   * is only established by a backend response (whoami / SSO exchange /
   * MFA verify) — see the client-state-spoofing QA scenario in tasks.md 4.1.
   *
   * @private
   */
  private initializeProviderListeners(): void {
    // Listen to Microsoft token-acquisition state changes (token step only)
    if (isProviderEnabled('microsoft')) {
      this.microsoftAuthService.isAuthenticated$
        .pipe(takeUntil(this.destroy$))
        .subscribe((isAuth) => {
          if (!isAuth && this._currentProvider.value === 'microsoft') {
            this.clearAuthenticationState();
          }
        });
    }

    if (isDebugMode()) {
      this.logger.debug('Auth Service: Provider listeners initialized');
    }
  }

  /**
   * Check initial authentication state by asking the BACKEND
   *
   * Calls `GET /parent-auth/whoami` (the session cookie rides automatically
   * via `withCredentials: true`). This is the ONLY source of truth for
   * authenticated state at boot — stale MSAL/Google SDK state in
   * localStorage is ignored, so a forged or leftover SDK state can never
   * make the app render an authenticated UI without a valid backend session.
   *
   * Failure modes (401, 500, network timeout) all land in the same clean
   * unauthenticated state — there is no half-authenticated condition.
   *
   * BOOT RACE (task 4.3, Defect 1): this whoami runs concurrently with MSAL's
   * redirect resolution. If the redirect exchange succeeds, its whoami
   * confirmation marks the boot whoami as superseded — the boot whoami's 401
   * (which fired before the session cookie existed) must NOT clear the
   * session the exchange just established.
   *
   * @private
   */
  private checkInitialAuthState(): void {
    this.whoami()
      .pipe(
        catchError((error: unknown) => {
          if (isDebugMode()) {
            this.logger.debug('Auth Service: whoami did not yield a session:', error);
          }
          return of(null);
        }),
      )
      .subscribe((user) => {
        if (user) {
          this.bootWhoamiInFlight = false;
          this.setAuthenticatedUser(user);
        } else if (
          this.bootWhoamiInFlight &&
          !this.exchangeInProgress &&
          !this._isAuthenticated.value
        ) {
          // No session at boot AND no exchange is in flight or has succeeded —
          // safe to land in the clean unauthenticated state.
          //
          // The `_isAuthenticated` check is what covers a COMPLETED exchange:
          // `exchangeInProgress` is reset in the exchange's `finally`, so on
          // its own it only guards an in-flight exchange. A boot 401 arriving
          // after the exchange resolved would otherwise clear the session that
          // exchange just established. This also protects a password/MFA login
          // that completes while the boot whoami is still outstanding.
          this.bootWhoamiInFlight = false;
          this.clearAuthenticationState();
        } else {
          // An exchange is in flight or already established a session; its
          // outcome decides the state. Do not clear here (race guard).
          if (isDebugMode()) {
            this.logger.debug(
              'Auth Service: Boot whoami 401 superseded by in-flight/completed SSO exchange',
            );
          }
        }
      });
  }

  /**
   * Wire the Microsoft redirect result to the backend exchange
   *
   * Task 4.3, Defect 1: `exchangeMicrosoftIdToken()` previously had ZERO
   * callers — a parent completing the Microsoft redirect landed
   * unauthenticated with no error. This subscription closes that loop:
   *
   * 1. MicrosoftAuthService publishes the redirect `AuthenticationResult`
   *    (including `result.idToken`) via `redirectResult$`.
   * 2. On a result with an ID token, this service runs the exchange
   *    (`POST /parent-auth/sso/microsoft`), matching how the login component
   *    handles the Google credential.
   * 3. On success: the session is established (via the exchange's whoami
   *    confirmation) and the MSAL cache is PURGED — the ID token is
   *    single-purpose and spent (task 4.3, Defect 2).
   * 4. On failure: a visible `SsoExchangeError` is emitted on
   *    `ssoExchangeError$` — never a silent return to the login screen.
   *
   * Direction of dependency: AuthService already injects
   * MicrosoftAuthService; MicrosoftAuthService does NOT inject AuthService
   * (that would be a circular DI dependency).
   *
   * @private
   */
  private initializeMicrosoftRedirectExchange(): void {
    if (!isProviderEnabled('microsoft')) {
      return;
    }

    this.microsoftAuthService.redirectResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (!result) {
            // Plain page load, not a redirect round-trip — nothing to
            // exchange. Also covers a hash-detection false positive.
            this._exchangeInProgress.next(false);
            return;
          }
          // SSO-LINK PENDING: this redirect belongs to the account-setup
          // first-time LINK flow, not a login. Capture the ID token for the
          // resume component (which may subscribe after this emission — the
          // two-page-load race) and stay out of the way: exchanging would
          // just 401 (no connection row exists yet) and surface a spurious
          // error. The resume consumes the captured credential one-shot.
          if (this.ssoLinkBridge.peek() !== null) {
            this._exchangeInProgress.next(false);
            const linkIdToken = result.idToken;
            if (linkIdToken) {
              this.ssoLinkBridge.captureCredential(linkIdToken);
            }
            if (isDebugMode()) {
              this.logger.debug(
                'Auth Service: Microsoft redirect captured for pending account-setup SSO link',
              );
            }
            return;
          }
          const idToken = result.idToken;
          if (!idToken) {
            this._exchangeInProgress.next(false);
            this.logger.error(
              'Auth Service: Microsoft redirect completed without an ID token — cannot establish a session',
            );
            this._ssoExchangeError.next(
              new SsoExchangeError(
                'Microsoft sign-in did not return an identity token. Please try again.',
                'microsoft',
              ),
            );
            return;
          }
          void this.exchangeMicrosoftRedirect(idToken);
        },
        error: (error: unknown) => {
          // The redirect round-trip itself failed — a visible failed login.
          this._exchangeInProgress.next(false);
          this.logger.error('Auth Service: Microsoft redirect failed:', error);
          this._ssoExchangeError.next(
            new SsoExchangeError(
              'Microsoft sign-in failed. Please try again.',
              'microsoft',
              error,
            ),
          );
        },
      });
  }

  /**
   * Exchange the Microsoft redirect's ID token for a backend session
   *
   * Wraps `exchangeMicrosoftIdToken()` with the boot-race guard and the
   * post-success MSAL cache purge.
   *
   * @private
   */
  private async exchangeMicrosoftRedirect(idToken: string): Promise<void> {
    this.exchangeInProgress = true;
    this._exchangeInProgress.next(true);
    try {
      // defaultValue: the MFA-required path (exchangeSsoToken) completes
      // WITHOUT emitting by design (the challenge is staged and
      // ssoMfaChallenge$ has already routed the UI away) — plain
      // firstValueFrom() throws EmptyError on that legitimate completion,
      // which this catch block then logged as a failed exchange on a
      // successful login (observed live 2026-09-15).
      await firstValueFrom(this.exchangeMicrosoftIdToken(idToken), { defaultValue: undefined });
      // The exchange's whoami confirmation has established the session and
      // cleared the boot-race guard (applyWhoamiUser → setAuthenticatedUser).
      // The MSAL cache is now spent — purge it (task 4.3, Defect 2). This
      // runs strictly after handleRedirectObservable() resolved (we are in
      // its next handler), so the redirect round-trip is complete.
      await this.microsoftAuthService.purgeMsalCache();
    } catch (error: unknown) {
      this.logger.error('Auth Service: Microsoft SSO exchange failed:', error);
      this._ssoExchangeError.next(
        new SsoExchangeError(
          'Microsoft sign-in could not be completed. Please try again or use another sign-in method.',
          'microsoft',
          error,
        ),
      );
    } finally {
      this.exchangeInProgress = false;
      this._exchangeInProgress.next(false);
    }
  }

  /**
   * Ask the backend whether a valid session cookie exists
   *
   * @returns Observable emitting the UnifiedUser when a session exists;
   *          an error (401/500/network) when it does not.
   */
  whoami(): Observable<UnifiedUser> {
    return this.http
      .get<BackendEnvelope<WhoAmIResponse>>(`${this.apiUrl}/parent-auth/whoami`, {
        withCredentials: true,
      })
      .pipe(mapEnvelope((data) => this.whoamiToUnifiedUser(data)));
  }

  /**
   * 3.8 (DCMPDEV-684): RAW whoami — same request as whoami() but keeps the
   * backend envelope intact, so the My-enrollment page can read the 2.7
   * pending-parent fields (pendingEnrollment, enrollments[]) that
   * whoamiToUnifiedUser drops.
   */
  /**
   * 4.15 (DCM-366): change the signed-in parent's password. 400 = too short,
   * 409 = SSO-only account, 401 = wrong current password (server messages are
   * surfaced verbatim).
   */
  changePassword(currentPassword: string, newPassword: string): Observable<unknown> {
    return this.http.post<BackendEnvelope<unknown>>(
      `${this.apiUrl}/parent-auth/change-password`,
      { currentPassword, newPassword },
      { withCredentials: true },
    );
  }

  whoamiRaw(): Observable<WhoAmIResponse> {
    return this.http
      .get<BackendEnvelope<WhoAmIResponse>>(`${this.apiUrl}/parent-auth/whoami`, {
        withCredentials: true,
      })
      .pipe(mapEnvelope((data) => data));
  }

  /**
   * Apply a whoami-confirmed user to service state
   *
   * Single funnel for flipping to authenticated state — used by the boot
   * check and by the post-exchange/post-verify whoami confirmation, so
   * authenticated state is ALWAYS derived from the same backend check.
   *
   * @internal Exposed for the module-level switchMapWhoami helper.
   */
  applyWhoamiUser(user: UnifiedUser): void {
    this.setAuthenticatedUser(user);
  }

  /**
   * Mark the boot whoami as superseded by a successful exchange
   *
   * @internal Exposed for the module-level switchMapWhoami helper (task 4.3
   * boot-race guard: a successful exchange's whoami confirmation wins over
   * the in-flight boot whoami's 401).
   */
  markExchangeConfirmed(): void {
    this.bootWhoamiInFlight = false;
  }

  /**
   * Map a whoami response to the unified user shape
   *
   * @private
   */
  private whoamiToUnifiedUser(data: WhoAmIResponse): UnifiedUser {
    const provider: AuthProvider = data.ssoConnections.some((c) => c.provider === 'microsoft')
      ? 'microsoft'
      : data.ssoConnections.some((c) => c.provider === 'google')
        ? 'google'
        : 'microsoft';
    return {
      id: String(data.contact.id),
      email: data.contact.email,
      name: data.contact.name,
      displayName: data.contact.displayName || data.contact.name || data.contact.email,
      provider,
      originalProfile: {
        id: String(data.contact.id),
        name: data.contact.name,
        email: data.contact.email,
      },
      mfaStatus: data.mfaStatus,
    };
  }

  /**
   * Set authenticated state from a backend-confirmed user
   *
   * @private
   */
  private setAuthenticatedUser(user: UnifiedUser): void {
    this._userProfile.next(user);
    this._currentProvider.next(user.provider);
    this._isAuthenticated.next(true);

    // Keep the global auth state cache informed (no tokens involved).
    const userProfile: UserProfile = {
      id: parseInt(user.id, 10) || 0,
      username: user.email,
      email: user.email,
      firstName: user.name.split(' ')[0] || user.displayName,
      lastName: user.name.split(' ').slice(1).join(' ') || undefined,
    };
    this.globalAuthState.setMicrosoftAuthState({
      isAuthenticated: true,
      user: userProfile,
    });

    if (isDebugMode()) {
      this.logger.debug(`Auth Service: User authenticated via ${user.provider}:`, user);
    }
  }

  /**
   * Clear authentication state
   *
   * Resets all authentication-related state observables. Safe to call
   * repeatedly; leaves no half-authenticated condition behind.
   *
   * @private
   */
  private clearAuthenticationState(): void {
    this._isAuthenticated.next(false);
    this._userProfile.next(null);
    this._currentProvider.next(null);

    // Also clear the global authentication cache so other services know user is logged out
    this.globalAuthState.clearAuthState();

    if (isDebugMode()) {
      this.logger.debug('Auth Service: Authentication state cleared');
    }
  }

  // ===========================================================================
  // Password + TOTP MFA login (backend endpoints from Phase 2, task 2.4)
  // ===========================================================================

  /**
   * Password login — step 1 of the mandatory-MFA flow
   *
   * POSTs `{ email, password }` to `/parent-auth/login` (request body only —
   * never a query string). On a correct password the backend responds with
   * `{ mfaRequired: true, challengeToken, hasPasskey? }` and NO session; the
   * challenge token must be kept in component state only and completed via
   * `verifyMfa()` (TOTP) or the passkey assertion flow. Unknown email /
   * wrong password / locked account all surface as the same generic error
   * (anti-enumeration).
   */
  loginPassword(email: string, password: string): Observable<LoginResult> {
    return this.http
      .post<BackendEnvelope<LoginResult>>(
        `${this.apiUrl}/parent-auth/login`,
        { email, password },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<LoginResult, LoginResult>((data) => data));
  }

  /**
   * Verify the TOTP code — step 2 of the password + MFA flow
   *
   * POSTs `{ challengeToken, code }` to `/parent-auth/mfa/verify`. On success
   * the backend sets the session cookie and this service flips to
   * authenticated state. The challenge token and code are never persisted.
   */
  verifyMfa(challengeToken: string, code: string): Observable<UnifiedUser> {
    return this.http
      .post<BackendEnvelope<Record<string, unknown>>>(
        `${this.apiUrl}/parent-auth/mfa/verify`,
        { challengeToken, code },
        { withCredentials: true },
      )
      .pipe(
        mapEnvelope<Record<string, unknown>, void>(() => undefined),
        // The verify response carries only {contactId, sessionId}; the real
        // profile comes from whoami with the fresh session cookie.
        switchMapWhoami(this),
      );
  }

  // ===========================================================================
  // MFA enrollment (task 5.1/5.2 — account-setup routes into mandatory MFA)
  // ===========================================================================

  /**
   * Generate a new TOTP secret for enrollment
   *
   * POSTs `{ challengeToken }` to `/parent-auth/mfa/enroll`. Only an
   * 'mfa_enroll'-purpose challenge is accepted (first-time setup, abandoned
   * enrollment, or admin MFA reset — never an account with a confirmed
   * secret). Returns the `otpauth://` URI for QR rendering. The secret never
   * touches this client in raw form — it lives inside the URI the backend
   * returns and is held in component state only.
   */
  mfaEnroll(challengeToken: string): Observable<MfaEnrollResult> {
    return this.http
      .post<BackendEnvelope<MfaEnrollResult>>(
        `${this.apiUrl}/parent-auth/mfa/enroll`,
        { challengeToken },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<MfaEnrollResult, MfaEnrollResult>((data) => data));
  }

  // ===========================================================================
  // Passkey registration + management (add-parent-portal-passkey-mfa, task 1.6)
  // ===========================================================================

  /**
   * Mint WebAuthn registration options for a new passkey
   *
   * POSTs to `/parent-auth/passkey/registration-options` (session-cookie
   * gated). Returns the serialized PublicKeyCredentialCreationOptions JSON —
   * pass it verbatim to `startRegistration()` from `@simplewebauthn/browser`
   * and keep it in component state for the verify call. Never persisted.
   */
  passkeyRegistrationOptions(): Observable<PasskeyRegistrationOptions> {
    return this.http
      .post<BackendEnvelope<PasskeyRegistrationOptions>>(
        `${this.apiUrl}/parent-auth/passkey/registration-options`,
        {},
        { withCredentials: true },
      )
      .pipe(
        mapEnvelope<PasskeyRegistrationOptions, PasskeyRegistrationOptions>((data) => data),
      );
  }

  /**
   * Verify a completed passkey registration ceremony
   *
   * POSTs `{ publicKeyCredentialOptions, publicKeyCredential }` to
   * `/parent-auth/passkey/registration-verify`. The options are the JSON from
   * `passkeyRegistrationOptions()`; the credential is the JSON-serialized
   * PublicKeyCredential returned by `startRegistration()`. On success the
   * backend stores the credential row scoped to the session contact.
   */
  passkeyRegistrationVerify(
    publicKeyCredentialOptions: string,
    publicKeyCredential: string,
  ): Observable<PasskeyRegistrationResult> {
    return this.http
      .post<BackendEnvelope<PasskeyRegistrationResult>>(
        `${this.apiUrl}/parent-auth/passkey/registration-verify`,
        { publicKeyCredentialOptions, publicKeyCredential },
        { withCredentials: true },
      )
      .pipe(
        mapEnvelope<PasskeyRegistrationResult, PasskeyRegistrationResult>((data) => data),
      );
  }

  /**
   * List the signed-in parent's own active passkeys
   *
   * GETs `/parent-auth/passkey/credentials`. The backend scopes the list to
   * the session contact — another contact's credentials are unreachable.
   */
  listPasskeys(): Observable<PasskeyCredential[]> {
    return this.http
      .get<BackendEnvelope<{ credentials: PasskeyCredential[] }>>(
        `${this.apiUrl}/parent-auth/passkey/credentials`,
        { withCredentials: true },
      )
      .pipe(
        mapEnvelope<{ credentials: PasskeyCredential[] }, PasskeyCredential[]>(
          (data) => data.credentials ?? [],
        ),
      );
  }

  /**
   * Rename one of the signed-in parent's own passkeys
   *
   * POSTs `{ nickname }` to `/parent-auth/passkey/credentials/{id}/rename`.
   * The backend matches the row id AND the session contact — a foreign id is
   * a generic 401.
   */
  renamePasskey(id: number, nickname: string): Observable<{ renamed: boolean }> {
    return this.http
      .post<BackendEnvelope<{ renamed: boolean }>>(
        `${this.apiUrl}/parent-auth/passkey/credentials/${id}/rename`,
        { nickname },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<{ renamed: boolean }, { renamed: boolean }>((data) => data));
  }

  /**
   * Remove one of the signed-in parent's own passkeys (step-up gated)
   *
   * POSTs `{ stepUpToken }` to `/parent-auth/passkey/credentials/{id}/remove`.
   * The stepUpToken comes from `passkeyStepUp()` (a fresh MFA verification
   * bound to the 'passkey_remove' action) and is consumed single-use. The
   * backend rejects with 409 when the removal would leave zero active MFA
   * methods.
   */
  removePasskey(id: number, stepUpToken: string): Observable<{ removed: boolean }> {
    return this.http
      .post<BackendEnvelope<{ removed: boolean }>>(
        `${this.apiUrl}/parent-auth/passkey/credentials/${id}/remove`,
        { stepUpToken },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<{ removed: boolean }, { removed: boolean }>((data) => data));
  }

  /**
   * Disable the signed-in parent's TOTP (step-up gated) — Phase 2, task 2.6
   *
   * POSTs `{ stepUpToken }` to `/parent-auth/mfa/disable`. The stepUpToken
   * comes from `passkeyStepUp()` bound to the 'totp_disable' action and is
   * consumed single-use. The backend rejects with 409 when TOTP is the last
   * remaining active MFA method.
   */
  disableTotp(stepUpToken: string): Observable<{ disabled: boolean }> {
    return this.http
      .post<BackendEnvelope<{ disabled: boolean }>>(
        `${this.apiUrl}/parent-auth/mfa/disable`,
        { stepUpToken },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<{ disabled: boolean }, { disabled: boolean }>((data) => data));
  }

  /**
   * 4.20 (DCM-366): POST `{ stepUpToken }` to `/parent-auth/mfa/reset` — forget
   * the current authenticator (a new phone). The token comes from
   * `passkeyStepUp(code, 'totp_reset')`; the backend revokes ALL sessions, so
   * the caller sends the parent to /login (where the QR enrol flow starts).
   */
  resetMfa(stepUpToken: string): Observable<{ reset: boolean }> {
    return this.http
      .post<BackendEnvelope<{ reset: boolean }>>(
        `${this.apiUrl}/parent-auth/mfa/reset`,
        { stepUpToken },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<{ reset: boolean }, { reset: boolean }>((data) => data));
  }

  /**
   * Step-up re-authentication: verify a fresh TOTP code, mint an action proof
   *
   * POSTs `{ code, action }` to `/parent-auth/passkey/step-up` (session-cookie
   * gated). On success returns a SHORT-TTL (2 min) 'security_action' challenge
   * token bound to the requested action — consumed single-use by the action
   * endpoint (e.g. `removePasskey()`). Failed verifications count toward the
   * same rate-limit bucket as login MFA failures. The code and token are held
   * in component state only.
   */
  passkeyStepUp(
    code: string,
    action: 'passkey_remove' | 'totp_disable' | 'totp_reset',
  ): Observable<PasskeyStepUpResult> {
    return this.http
      .post<BackendEnvelope<PasskeyStepUpResult>>(
        `${this.apiUrl}/parent-auth/passkey/step-up`,
        { code, action },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<PasskeyStepUpResult, PasskeyStepUpResult>((data) => data));
  }

  // ===========================================================================
  // Passkey login-time assertion (Phase 2, task 2.2/2.5)
  // ===========================================================================

  /**
   * Mint WebAuthn assertion options at login time
   *
   * POSTs `{ challengeToken }` (the step-1 token from loginPassword/SSO) to
   * `/parent-auth/passkey/authentication-options`. Returns the browser-facing
   * publicKeyCredentialRequestOptions JSON (pass verbatim to
   * `startAuthentication()` from `@simplewebauthn/browser`) plus the
   * ceremony token (`waChallengeToken`) for the verify call. Both are held
   * in component state only — never persisted.
   */
  passkeyAuthenticationOptions(challengeToken: string): Observable<PasskeyAuthenticationOptions> {
    return this.http
      .post<BackendEnvelope<PasskeyAuthenticationOptions>>(
        `${this.apiUrl}/parent-auth/passkey/authentication-options`,
        { challengeToken },
        { withCredentials: true },
      )
      .pipe(
        mapEnvelope<PasskeyAuthenticationOptions, PasskeyAuthenticationOptions>((data) => data),
      );
  }

  /**
   * Verify a completed passkey assertion ceremony and create the session
   *
   * POSTs `{ waChallengeToken, assertionResponse }` to
   * `/parent-auth/passkey/authentication-verify`. The token is the ceremony
   * token from `passkeyAuthenticationOptions()`; the assertion is the
   * JSON-serialized PublicKeyCredential returned by `startAuthentication()`.
   * On success the backend sets the session cookie (identical shape to a
   * TOTP-completed login).
   */
  passkeyAuthenticationVerify(
    waChallengeToken: string,
    assertionResponse: string,
  ): Observable<UnifiedUser> {
    return this.http
      .post<BackendEnvelope<Record<string, unknown>>>(
        `${this.apiUrl}/parent-auth/passkey/authentication-verify`,
        { waChallengeToken, assertionResponse },
        { withCredentials: true },
      )
      .pipe(
        mapEnvelope<Record<string, unknown>, void>(() => undefined),
        // The verify response carries only {contactId, sessionId}; the real
        // profile comes from whoami with the fresh session cookie — the same
        // confirmation chain as verifyMfa().
        switchMapWhoami(this),
      );
  }

  // ===========================================================================
  // Account provisioning (task 5.2 — backend endpoints from Phase 2, task 2.5)
  // ===========================================================================

  /**
   * Fetch account-setup context for an invite token
   *
   * GETs `/parent-auth/account-setup/{token}`. The backend validates the
   * token WITHOUT consuming it (display context only) and returns the
   * invited contact's email. The token stays in the route/component state.
   */
  accountSetupContext(token: string): Observable<AccountSetupContext> {
    return this.http
      .get<BackendEnvelope<AccountSetupContext>>(
        `${this.apiUrl}/parent-auth/account-setup/${encodeURIComponent(token)}`,
        { withCredentials: true },
      )
      .pipe(mapEnvelope<AccountSetupContext, AccountSetupContext>((data) => data));
  }

  /**
   * Complete account setup — set password and/or link SSO
   *
   * POSTs `{ token, password? }` to `/parent-auth/account-setup`. The backend
   * consumes the token ATOMICALLY (single-use) and routes into mandatory MFA
   * enrollment: the response carries `{ mfaRequired: true,
   * mfaEnrollmentRequired: true, challengeToken }` and NO session. The
   * challenge token is returned to the caller (component state only) to
   * drive the enrollment step.
   *
   * Note: the SSO-link variant lives in `completeAccountSetupSso()` below —
   * the account-setup UI offers both credential paths.
   */
  completeAccountSetup(token: string, password: string): Observable<LoginResult> {
    return this.http
      .post<BackendEnvelope<LoginResult>>(
        `${this.apiUrl}/parent-auth/account-setup`,
        { token, password },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<LoginResult, LoginResult>((data) => data));
  }

  /**
   * Complete account setup — link an SSO identity (first-time link)
   *
   * POSTs `{ token, connectSso: true, ssoIdToken }` to
   * `/parent-auth/account-setup`. The backend verifies the SSO ID token
   * server-side (ParentMsalToken / GoogleIDToken), REQUIRES its email claim
   * to match the invited contact's email (the admin vouched for that address
   * by issuing the invite), stores the durable subject in
   * ParentSsoConnections, consumes the token atomically, and routes into
   * mandatory MFA enrollment — same response shape as the password path.
   *
   * This is the ONLY frontend path that sends `connectSso`; the backend
   * treats it as the sole place the email claim is trusted.
   */
  completeAccountSetupSso(token: string, ssoIdToken: string): Observable<LoginResult> {
    return this.http
      .post<BackendEnvelope<LoginResult>>(
        `${this.apiUrl}/parent-auth/account-setup`,
        { token, connectSso: true, ssoIdToken },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<LoginResult, LoginResult>((data) => data));
  }

  /**
   * Request a password-reset email
   *
   * POSTs `{ email }` to `/parent-auth/forgot-password`. The backend returns
   * the SAME response for known and unknown emails (anti-enumeration) — the
   * UI must present one constant message regardless of outcome.
   */
  forgotPassword(email: string): Observable<ForgotPasswordResult> {
    return this.http
      .post<BackendEnvelope<ForgotPasswordResult>>(
        `${this.apiUrl}/parent-auth/forgot-password`,
        { email },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<ForgotPasswordResult, ForgotPasswordResult>((data) => data));
  }

  /**
   * Complete a password reset with a single-use token
   *
   * POSTs `{ token, newPassword }` to `/parent-auth/reset-password`. The
   * backend consumes the token atomically, revokes ALL existing sessions for
   * the contact, and does NOT establish a session (no auto-login) — the
   * parent must log in fresh. The token lives in the route/component state
   * only and is single-use server-side.
   */
  resetPassword(token: string, newPassword: string): Observable<ResetPasswordResult> {
    return this.http
      .post<BackendEnvelope<ResetPasswordResult>>(
        `${this.apiUrl}/parent-auth/reset-password`,
        { token, newPassword },
        { withCredentials: true },
      )
      .pipe(mapEnvelope<ResetPasswordResult, ResetPasswordResult>((data) => data));
  }

  // ===========================================================================
  // SSO logins (token acquisition client-side, exchange server-side)
  // ===========================================================================

  /**
   * Exchange a Microsoft ID token for a backend session
   *
   * POSTs the MSAL-acquired ID token to `/parent-auth/sso/microsoft`. The
   * backend verifies it server-side (ParentMsalToken.cfc: signature, aud,
   * tenant allowlist) and sets the session cookie on success.
   */
  exchangeMicrosoftIdToken(idToken: string): Observable<UnifiedUser> {
    return this.exchangeSsoToken('microsoft', idToken);
  }

  /**
   * Exchange a Google ID token for a backend session
   *
   * POSTs the GIS `credential` (ID token) to `/parent-auth/sso/google`. The
   * backend verifies it server-side (GoogleIDToken.cfc: JWKS signature, aud,
   * iss, exp) and sets the session cookie on success.
   */
  exchangeGoogleIdToken(idToken: string): Observable<UnifiedUser> {
    return this.exchangeSsoToken('google', idToken);
  }

  /**
   * Shared SSO token exchange
   *
   * @private
   */
  private exchangeSsoToken(provider: 'microsoft' | 'google', idToken: string): Observable<UnifiedUser> {
    return this.http
      .post<BackendEnvelope<LoginResult>>(
        `${this.apiUrl}/parent-auth/sso/${provider}`,
        { id_token: idToken },
        { withCredentials: true },
      )
      .pipe(
        // The backend ships this response UNWRAPPED (createResponseHeader
        // puts the payload at the top level for 2xx) — mapEnvelope handles
        // both that bare shape and a `{ data }` envelope. Using the raw
        // envelope here previously (looking only at `envelope.data`) meant
        // `mfaRequired` never matched against the actual bare payload, so
        // every successful SSO login fell through to whoami() and 401'd as
        // a spurious "exchange failed" (observed live 2026-09-15).
        mapEnvelope<LoginResult, LoginResult>((data) => data),
        // MANDATORY MFA (design.md Decision 2): the SSO exchange NEVER
        // creates a session — it returns { mfaRequired, challengeToken }
        // (plus mfaEnrollmentRequired when no confirmed TOTP secret
        // exists). Stage the challenge exactly like the password path,
        // emit ssoMfaChallenge$ so the login screen routes to
        // /mfa-challenge, and STOP — the whoami chain must NOT run (there
        // is no session yet; its 401 would surface as a failed exchange —
        // the user-visible "exchange failed" error on a successful login).
        switchMap((data) => {
          if (data.mfaRequired === true && typeof data.challengeToken === 'string') {
            MfaChallengeBridge.stage(
              data.challengeToken,
              data.mfaEnrollmentRequired ? 'enroll' : 'verify',
              data.hasPasskey === true,
            );
            this._ssoMfaChallenge.next({ provider });
            return EMPTY;
          }
          return of(data);
        }),
        // Session-established path only: confirm via whoami.
        switchMap(() => this.whoami().pipe(
          tap((user) => {
            this.markExchangeConfirmed();
            this.applyWhoamiUser(user);
          }),
        )),
      );
  }

  /**
   * Microsoft Azure AD authentication
   *
   * Delegates to MicrosoftAuthService for the MSAL redirect. The resulting
   * ID token is exchanged for a backend session by the SSO callback handling
   * (microsoft-auth.service.ts → AuthService.exchangeMicrosoftIdToken).
   *
   * @returns Promise that resolves once redirect is initiated
   */
  async loginMicrosoft(options?: { returnUrl?: string }): Promise<void> {
    if (!isProviderEnabled('microsoft')) {
      throw new Error('Microsoft authentication is disabled');
    }

    try {
      if (isDebugMode()) {
        this.logger.debug('Auth Service: Starting Microsoft login');
      }

      // Optionally persist return URL for post-login navigation
      if (options?.returnUrl) {
        sessionStorage.setItem('starter-post-login-redirect', options.returnUrl);
      }

      // Ensure MSAL is ready before initiating redirect
      await this.microsoftAuthService.initialize();
      this.microsoftAuthService.signInWithRedirect();
    } catch (error) {
      if (isDebugMode()) {
        this.logger.error('Auth Service: Microsoft login failed:', error);
      }
      throw error;
    }
  }

  /**
   * Clear Microsoft authentication data
   *
   * Clears all Microsoft authentication state and cached tokens.
   * Useful for resolving authentication issues.
   */
  async clearMicrosoftAuthenticationData(): Promise<void> {
    try {
      await this.microsoftAuthService.clearAllAuthenticationData();
      this.globalAuthState.clearAuthState();

      if (isDebugMode()) {
        this.logger.debug('Auth Service: Microsoft authentication data cleared');
      }
    } catch (error) {
      this.logger.error('Auth Service: Error clearing Microsoft authentication data:', error);
      throw error;
    }
  }

  /**
   * Google OAuth 2.0 authentication
   *
   * Delegates to GoogleAuthService for the GIS flow. The resulting ID token
   * is exchanged for a backend session via `exchangeGoogleIdToken()`.
   *
   * @returns Promise that resolves once the GIS flow is initiated
   */
  async loginGoogle(options?: { returnUrl?: string }): Promise<void> {
    if (!isProviderEnabled('google')) {
      throw new Error('Google authentication is disabled');
    }

    try {
      if (isDebugMode()) {
        this.logger.debug('Auth Service: Starting Google login');
      }

      if (options?.returnUrl) {
        sessionStorage.setItem('starter-post-login-redirect', options.returnUrl);
      }

      await this.googleAuthService.signInWithRedirect();
    } catch (error) {
      if (isDebugMode()) {
        this.logger.error('Auth Service: Google login failed:', error);
      }
      throw error;
    }
  }

  /**
   * Sign out the current user
   *
   * Always POSTs `/parent-auth/logout` first so the SERVER-side session is
   * revoked (the httpOnly cookie cannot be cleared client-side in any
   * meaningful way — revocation is a backend act). Client state is cleared
   * unconditionally afterwards, even when the logout call fails (network
   * down, 500, etc.) — a failed backend call must never leave the UI in
   * authenticated state. The Microsoft SDK sign-out then clears MSAL's own
   * cached state.
   */
  async logout(): Promise<void> {
    const currentProvider = this._currentProvider.value;

    try {
      if (isDebugMode()) {
        this.logger.debug(`Auth Service: Starting logout for provider: ${currentProvider}`);
      }

      // Revoke the server-side session. Failures are logged but never block
      // client-state cleanup (see the failure-modes QA scenario in 4.1).
      try {
        await firstValueFrom(
          this.http.post<BackendEnvelope<{ loggedOut: boolean }>>(
            `${this.apiUrl}/parent-auth/logout`,
            {},
            { withCredentials: true },
          ),
        );
      } catch (logoutError) {
        this.logger.error('Auth Service: Backend logout call failed:', logoutError);
      }

      // Clear unified auth state immediately so UI updates even if the
      // provider sign-out below navigates away.
      this.clearAuthenticationState();

      if (currentProvider === 'microsoft' && isProviderEnabled('microsoft')) {
        await firstValueFrom(this.microsoftAuthService.signOut());
      } else if (currentProvider === 'google' && isProviderEnabled('google')) {
        await this.googleAuthService.signOut();
      }

      if (isDebugMode()) {
        this.logger.debug('Auth Service: Logout completed successfully');
      }
    } catch (error: unknown) {
      if (isDebugMode()) {
        this.logger.error('Auth Service: Logout failed:', error);
      }

      // For any error, clear authentication state — never leave the UI
      // authenticated after a logout attempt.
      this.clearAuthenticationState();
      throw error;
    }
  }

  /**
   * 4.7 (R29): the interceptor calls this when a 401 reveals the parent's
   * session has ended while the client still shows signed-in state.
   *
   * Clears the STALE authenticated state (no HTTP call — the backend already
   * refused the request) so guards let the parent land on /login: guestGuard
   * would otherwise see the stale state and bounce them back into the dead
   * session. Safe to call repeatedly.
   */
  markSessionExpired(): void {
    if (this._isAuthenticated.value) {
      this.clearAuthenticationState();
    }
  }

  /**
   * Get the currently authenticated user (unified across all providers)
   */
  getCurrentUser(): UnifiedUser | null {
    return this._userProfile.value;
  }

  /**
   * Get the current authentication provider
   */
  getCurrentProvider(): AuthProvider | null {
    return this._currentProvider.value;
  }

  /**
   * Check if a user is currently authenticated (unified across all providers)
   */
  get isAuthenticated(): boolean {
    return this._isAuthenticated.value;
  }

  /**
   * Get the display name of the current user (unified across all providers)
   */
  getUserDisplayName(): string {
    const user = this.getCurrentUser();
    return user?.displayName || 'User';
  }

  /**
   * Get the email address of the current user (unified across all providers)
   */
  getUserEmail(): string {
    const user = this.getCurrentUser();
    return user?.email || '';
  }

  /**
   * Get provider-specific user data
   */
  getOriginalProfile(): MicrosoftUser | GoogleUser | null {
    const user = this.getCurrentUser();
    return user?.originalProfile || null;
  }

  /**
   * Check if a specific provider is currently active
   */
  isProviderActive(provider: AuthProvider): boolean {
    return this._currentProvider.value === provider;
  }

  /**
   * Clean up service resources and subscriptions
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.microsoftAuthService.destroy();
  }
}

/**
 * Unwrap the backend response envelope
 *
 * The backend's createResponseHeader() returns the payload DIRECTLY for
 * status codes <= 202 (e.g. `{ email }`, `{ mfaRequired, challengeToken }`)
 * and a `{ status, message }` body only for > 202 — it does NOT wrap
 * successes in `{ statusCode, data, statusMessage }`. This helper accepts
 * both shapes:
 *   - an envelope with a `data` key -> unwrap it (the documented contract);
 *   - a bare payload object (no `data`, no `statusCode` key) -> treat the
 *     whole body as the data (the shape the backend actually ships).
 * Anything else (empty body, non-object) is an error rather than silently
 * producing undefined state.
 *
 * Non-2xx statuses arrive as HttpErrorResponse (handled by callers).
 *
 * @private
 */
/**
 * Whether a URL fragment looks like an MSAL redirect response
 * (`response_mode=fragment`): `#code=...&state=...` on success or
 * `#error=...&state=...` on failure. Used to flip the settling-spinner
 * flag on synchronously, before MSAL's own async hash processing starts.
 */
function isMsalRedirectHash(hash: string): boolean {
  if (!hash || hash.length < 2) {
    return false;
  }
  const params = hash.startsWith('#') ? hash.slice(1) : hash;
  return /(^|&)(code|error)=/.test(params) && /(^|&)state=/.test(params);
}

function mapEnvelope<T, R>(mapData: (data: T) => R) {
  return (source: Observable<BackendEnvelope<T>>): Observable<R> =>
    new Observable<R>((subscriber) => {
      return source.subscribe({
        next: (envelope) => {
          if (!envelope || typeof envelope !== 'object') {
            subscriber.error(new Error('Malformed response from backend'));
            return;
          }
          if ('data' in envelope) {
            subscriber.next(mapData(envelope.data));
            return;
          }
          // Bare payload: the backend ships successes unwrapped. Reject only
          // bodies that are structurally an error/status object, not data.
          if ('statusCode' in envelope || 'status' in envelope) {
            subscriber.error(new Error('Malformed response from backend'));
            return;
          }
          subscriber.next(mapData(envelope as unknown as T));
        },
        error: (err: unknown) => subscriber.error(err),
        complete: () => subscriber.complete(),
      });
    });
}

/**
 * After a session-establishing call (SSO exchange / MFA verify), immediately
 * confirm the session with whoami so authenticated state is derived from the
 * same backend check used at boot — not from the shape of the login response.
 *
 * The confirmation also marks the boot whoami as superseded (task 4.3, Defect 1
 * boot race): a successful exchange's whoami wins over the in-flight boot
 * whoami's 401, which fired before the session cookie existed.
 *
 * @private
 */
function switchMapWhoami(auth: AuthService) {
  return (source: Observable<unknown>): Observable<UnifiedUser> =>
    new Observable<UnifiedUser>((subscriber) => {
      return source.subscribe({
        next: () => {
          auth
            .whoami()
            .pipe(
              tap((user) => {
                auth.markExchangeConfirmed();
                auth.applyWhoamiUser(user);
              }),
              catchError((whoamiError: unknown) => {
                // The exchange succeeded but whoami failed — treat as a
                // failed login rather than a half-authenticated state.
                return throwError(() => whoamiError);
              }),
            )
            .subscribe({
              next: (user) => subscriber.next(user),
              error: (err: unknown) => subscriber.error(err),
              complete: () => subscriber.complete(),
            });
        },
        error: (err: unknown) => subscriber.error(err),
      });
    });
}
